import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, DataTable, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AUDIT_SEVERITY_VARIANT, auditSeverityLabelKey } from '../constants';
import { formatRelativeTime, initialsOf } from '../utils';
import { Asterisk, Bot } from 'lucide-react';

// Prototype `.audit-event` — monospace event-code chip.
const EVENT_CHIP =
  'inline-flex items-center rounded-md border border-edge bg-white/[0.03] px-2 py-[3px] font-mono text-[12px] leading-none text-content-secondary';

// Prototype resource link — provider-tinted text (brand-blue design token).
const RESOURCE_LINK = 'font-medium text-[13px] text-brand-500';

// Prototype expanded-detail toolbar — subtle ghost-outline buttons, not filled.
const COPY_BTN =
  '!h-auto !border !border-edge !bg-transparent !px-2.5 !py-[5px] !text-[12px] !font-semibold !text-content-secondary hover:!border-edge-strong hover:!bg-transparent hover:!text-content';

const EM_DASH = <span className="text-content-faint">—</span>;

/** Absolute UTC stamp shown inside the expanded JSON ("2026-05-19 13:20:11 UTC"). */
const formatUtc = (iso) => new Date(iso).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');

/** Full read-only event payload revealed when a row is expanded (CT-4212). */
const toEventJson = (row) =>
  JSON.stringify(
    {
      timestamp: formatUtc(row.timestamp),
      severity: row.severity,
      event: row.event,
      provider: row.provider_name ?? row.provider_slug ?? null,
      instance: row.instance_name ?? null,
      actor: row.actor
        ? { name: row.actor.name, id: row.actor.id ?? null, ip: row.actor.ip ?? row.ip ?? null }
        : null,
      studio: row.studio,
      before: row.before ?? null,
      after: row.after ?? null,
      details: row.details ?? null,
    },
    null,
    2,
  );

/** Copy-as-cURL against the (future) audit endpoint for this event. */
const toCurl = (row, slug) =>
  `curl -H "Authorization: Bearer $AIMS_TOKEN" \\\n  https://api.aims-os.ai/v1/workspace/integrations/${row.provider_slug ?? row.resource ?? slug}/audit/${row.id}`;

/** Actor cell — bot icon for agents, asterisk for system, initials avatar for users. */
const ActorCell = ({ actor, t }) => {
  if (!actor) return EM_DASH;
  if (actor.kind === 'agent') {
    return (
      <span className="inline-flex items-center gap-2 text-[13px] text-content">
        <Bot size={16} strokeWidth={1.9} className="shrink-0 text-content-muted" aria-hidden="true" />
        {t('integrations.audit.actorAgent', { name: actor.name })}
      </span>
    );
  }
  if (actor.kind === 'system') {
    return (
      <span className="inline-flex items-center gap-2 text-[13px] text-content">
        <Asterisk size={16} strokeWidth={2} className="shrink-0 text-content-muted" aria-hidden="true" />
        {actor.name}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-[13px] text-content">
      <span
        aria-hidden="true"
        className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-brand-500/15 text-[10px] font-bold text-brand-500"
      >
        {initialsOf(actor.name)}
      </span>
      {actor.name}
    </span>
  );
};

/**
 * Audit log table (CT-4184) — the shared workspace `DataTable` configured with
 * the audit columns and an expandable row detail (raw event JSON + Copy event ID
 * / Copy as cURL). Kept as a standalone component so the Global Audit page
 * (CT-4210) can reuse it.
 *
 * @param {object} props
 * @param {object[]} props.rows — audit events, newest first.
 * @param {number} props.now — reference epoch for relative timestamps (captured off-render).
 * @param {string} props.slug — provider slug, used to build the cURL sample.
 * @param {string} props.emptyLabel — shown when `rows` is empty.
 */
const AuditTable = ({ rows, now, slug, emptyLabel }) => {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();

  const copy = (text) => {
    navigator.clipboard?.writeText?.(text);
    toast.info({ message: t('integrations.audit.copied'), canClose: true });
  };

  const columns = useMemo(
    () => [
      {
        // Extra left padding so the first column isn't glued to the card edge;
        // it insets the content only — the row/header background still fills.
        key: 'timestamp',
        label: t('integrations.audit.columns.timestamp'),
        headerClassName: 'w-[132px] !pl-5',
        cellClassName: '!pl-5',
        render: (value) => (
          <span className="whitespace-nowrap text-[13px] text-content-secondary">
            {formatRelativeTime(value, t, now)}
          </span>
        ),
      },
      {
        key: 'severity',
        label: t('integrations.audit.columns.severity'),
        headerClassName: 'w-[100px]',
        render: (value) => (
          <Badge variant={AUDIT_SEVERITY_VARIANT[value] ?? 'neutral'} size="small">
            {t(auditSeverityLabelKey(value))}
          </Badge>
        ),
      },
      {
        key: 'event',
        label: t('integrations.audit.columns.event'),
        headerClassName: 'w-[220px]',
        truncate: true,
        render: (value) => <span className={EVENT_CHIP}>{value}</span>,
      },
      {
        key: 'actor',
        label: t('integrations.audit.columns.actor'),
        headerClassName: 'w-[240px]',
        truncate: true,
        render: (value) => <ActorCell actor={value} t={t} />,
      },
      {
        key: 'resource',
        label: t('integrations.audit.columns.resource'),
        headerClassName: 'w-[140px]',
        truncate: true,
        render: (value, row) =>
          value ? (
            <Link
              to={`/admin-studio/integrations/${row.provider_slug ?? value}`}
              className={RESOURCE_LINK}
              onClick={(e) => e.stopPropagation()}
            >
              {value}
            </Link>
          ) : (
            EM_DASH
          ),
      },
      {
        key: 'studio',
        label: t('integrations.audit.columns.studio'),
        headerClassName: 'w-[120px]',
        render: (value) =>
          value ? (
            <span className="text-[13px] text-content-secondary">
              {t(`integrations.audit.studio.${value}`)}
            </span>
          ) : (
            EM_DASH
          ),
      },
      {
        key: 'ip',
        label: t('integrations.audit.columns.ip'),
        headerClassName: 'w-[120px]',
        render: (value) =>
          value ? (
            <span className="whitespace-nowrap font-mono text-[12px] text-content-secondary">{value}</span>
          ) : (
            EM_DASH
          ),
      },
    ],
    [t, now],
  );

  const renderRowDetail = (row) => (
    <div className="rounded-lg border border-edge bg-white/[0.02] p-3.5">
      <pre className="overflow-x-auto font-mono text-[12px] leading-[1.55] text-content-secondary">{toEventJson(row)}</pre>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="tertiary" size="sm" className={COPY_BTN} onClick={() => copy(row.id)}>
          {t('integrations.audit.copyId')}
        </Button>
        <Button variant="tertiary" size="sm" className={COPY_BTN} onClick={() => copy(toCurl(row, slug))}>
          {t('integrations.audit.copyCurl')}
        </Button>
      </div>
    </div>
  );

  return (
    <Card className="overflow-hidden">
      {/* Scoped to this usage only: in dark mode soften the white text
          (`text-content` → header + actor name) to 60% opacity. Badges, the blue
          resource link and the muted/secondary text keep their own colors. */}
      <div className="overflow-x-auto dark:[&_.text-content]:text-white/60">
        <DataTable
          variant="workspace"
          columns={columns}
          data={rows}
          minTableWidth={1060}
          emptyMessage={emptyLabel}
          renderRowDetail={renderRowDetail}
          expandRowAriaLabel={t('integrations.audit.expandRow')}
        />
      </div>
    </Card>
  );
};

export default AuditTable;
